/**
 * 푸시 알림 권한 화면의 판정 (DOM 없음 — tests/unit/push-permission-logic.test.mjs).
 *
 * 권한이 「거부」(denied)가 되면 브라우저는 다시 묻지 않는다 — Notification.requestPermission() 은 창을 띄우지 않고
 * 곧바로 denied 를 돌려준다(크롬 · 사파리 · 삼성 인터넷 모두). 그래서 거부된 기기에는 그 기기에서 켜는 곳을 짚어 준다.
 *
 * pushHelpCase — 켜는 곳이 다른 경우:
 *   android-app     — 안드로이드 설치 앱: 아이콘 길게 → 앱 정보 → 알림 (WebAPK 의 알림 권한은 안드로이드 앱 설정이 쥔다)
 *   ios-app         — 아이폰 · 아이패드 홈 화면 앱: 설정 앱 → 알림 → GROUPCREW
 *   desktop-app     — 데스크톱 설치 앱 창(탭 틀 포함): ⋮ → 앱 정보 → 알림
 *   android-browser — 폰 브라우저 탭: 주소창 왼쪽 사이트 정보 → 권한 → 알림
 *   desktop-browser — 데스크톱 브라우저 탭: 주소창 왼쪽 사이트 정보 → 알림
 *   ios-browser     — 아이폰 사파리 탭: 푸시 자체가 없다(홈 화면에 추가해야 한다) — 안내는 「홈 화면에 추가」
 *
 * relaunchHint — 켜고 돌아왔을 때 이 화면이 바뀐 권한을 볼 수 있는가:
 *   required — 안드로이드 설치 앱: 크롬은 WebAPK 의 안드로이드 알림 설정을 **앱이 시작할 때만** 읽어 사이트 권한에 얹는다
 *              (WebApkActivityCoordinator.onDeferredStartupWithStorage → PermissionUpdater.onWebApkLaunched — 돌아오기(resume)
 *              에는 다시 읽지 않는다). 그 값이 사이트 권한보다 앞서므로 앱 정보에서 켜고 돌아와도 Notification.permission 은
 *              denied 그대로이고, 페이지를 다시 불러도 같다 — 앱을 완전히 닫았다가 다시 열어야 한다(2026-10-02 기기에서 확인).
 *   maybe    — 아이폰 홈 화면 앱: 확인하지 못했다 — 돌아와도 안 되면 다시 열라고 덧붙인다
 *   none     — 브라우저 탭 · 데스크톱 앱 창: 사이트 정보에서 바꾸면 곧바로 바뀌고 권한 변화 이벤트가 온다
 *
 * pushUIState — 벨 메뉴 단추와 폰 「전체」 시트 항목이 같이 쓰는 상태:
 *   unsupported — 이 브라우저에는 푸시가 없다 (항목을 숨긴다)
 *   ios-install — 아이폰 브라우저 탭: 홈 화면에 추가한 앱에서만 된다
 *   on          — 이 기기는 구독돼 있다
 *   off         — 아직 켜지 않았다 (누르면 먼저 설명 → 브라우저 창)
 *   blocked     — 거부됨 (누르면 켜는 방법)
 */
import { isIOS } from './pwa-install-logic.js';

export function pushHelpCase({ ua = '', maxTouchPoints = 0, standalone = false, mobile = false } = {}) {
    const ios = isIOS(ua, maxTouchPoints);
    const phone = /Android/.test(ua) || mobile;
    if (standalone) return ios ? 'ios-app' : phone ? 'android-app' : 'desktop-app';
    if (ios) return 'ios-browser';
    return phone ? 'android-browser' : 'desktop-browser';
}

export function pushUIState({ supported = false, iosBrowser = false, permission = 'default', subscribed = false } = {}) {
    if (iosBrowser) return 'ios-install';
    if (!supported) return 'unsupported';
    if (permission === 'denied') return 'blocked';
    if (permission === 'granted' && subscribed) return 'on';
    return 'off';
}

// 권한 시트를 어떤 모양으로 열지: 거부면 켜는 방법, 아직 묻지 않았으면 먼저 설명. 허용이면 시트 없이 바로 구독한다(null).
export function pushSheetMode(permission) {
    if (permission === 'denied') return 'blocked';
    if (permission === 'granted') return null;
    return 'ask';
}

// 다른 곳(기기 설정 · 사이트 정보)에서 방금 허용으로 바뀌었는가 — 그때 이 탭이 조용히 구독을 잇는다.
export function permissionTurnedOn(prev, next) {
    return prev !== null && prev !== undefined && prev !== 'granted' && next === 'granted';
}

export function relaunchHint(kase) {
    if (kase === 'android-app') return 'required';
    if (kase === 'ios-app') return 'maybe';
    return 'none';
}
